// frontend/src/utils/serverDetail.js
//
// One rule for "should this screen show the server's own `detail`, or its own
// hardcoded sentence?".
//
// Lives here rather than beside any one caller because the same three screens
// hit it - the end-user groups tab and both admin group tables - and a second
// copy is how the two halves of a rule drift apart.

// The Hebrew Unicode block, written as escapes so the pattern survives any
// file-encoding accident.
const HEBREW_SCRIPT = /[\u0590-\u05FF]/;

/** Does this string contain Hebrew letters? Internal to the rule below. */
const containsHebrew = (value) =>
  typeof value === "string" && HEBREW_SCRIPT.test(value);

/**
 * Prefer the server's own sentence to a hardcoded one - WITHOUT matching on its
 * copy.
 *
 * An endpoint answers one status for several different facts (``PATCH
 * /groups/{id}`` answers 409 both for a duplicate address and for an address
 * that may not move at all), each carrying its own ``detail``. Hardcoding one
 * of those reasons states the wrong fact for every other one, and
 * string-matching the copy to tell them apart breaks the first time the backend
 * rewords a message.
 *
 * The one thing a ``detail`` cannot be trusted on is LANGUAGE. Our backend
 * writes some details in Hebrew (the private-group guards) and some in bare
 * English ("You have already added this group."), and neither is copy to put in
 * front of a user reading the other language. So the rule is script MATCHING,
 * in BOTH directions: show the server's sentence only when its script is the
 * script this UI is currently rendering, and fall back otherwise.
 *
 * Matching, not "at least as intelligible as ours": the earlier one-directional
 * form (``!isHebrew || containsHebrew(detail)``) short-circuited in the English
 * UI and handed an English-locale user a wall of Hebrew, because every one of
 * the private-group details is Hebrew-only. Both directions now fail the same
 * way, and the day a detail gets translated it starts surfacing with no change
 * here.
 *
 * Non-strings fall back too. FastAPI answers 422 with a `detail` LIST, and
 * rendering that array into JSX throws.
 *
 * @param {unknown} detail   ``err.response?.data?.detail``, whatever it is
 * @param {string} fallback  this screen's own wording for the common case
 * @param {boolean} isHebrew is the UI currently in Hebrew?
 * @returns {string}
 */
export const serverDetailOr = (detail, fallback, isHebrew) =>
  typeof detail === "string" &&
  detail.trim() !== "" &&
  containsHebrew(detail) === !!isHebrew
    ? detail
    : fallback;
